import React, { useState, useEffect } from 'react';
import { automunityAPI } from '../lib/api';
import toast, { Toaster } from 'react-hot-toast';
import './Monitor.css';

interface MonitoredFunction {
  integration_config_id: number;
  automation_id: number;
  automation_name: string;
  function_identifier: string;
  expected_schedule_description: string;
  latest_status: string;
  last_checked_at: string;
  last_invocation_count: number;
  last_error_count: number;
  monitoring_enabled: number;
}

interface MonitorSnapshot {
  snapshot_id: number;
  function_name: string;
  snapshot_time: string;
  status: string;
  invocations: number;
  errors: number;
  error_rate: number;
  avg_duration_ms: number;
  max_duration_ms: number;
  throttles: number;
}

interface MonitorAlert {
  alert_id: number;
  alert_key: string;
  source: string;
  function_name: string;
  automation_name: string;
  alert_type: string;
  status: string;
  app_name: string;
  app_env: string;
  app_abbr: string;
  message: string;
  error_summary: string;
  exception_type: string;
  identifier: string;
  severity: string;
  occurrence_count: number;
  last_occurrence_at: string;
  assigned_to: number | null;
  assigned_to_name: string | null;
  created_at: string;
  resolved_at: string | null;
  notes: string | null;
}

interface DiscoverResult {
  function_name: string;
  runtime: string;
  description: string;
  last_modified: string;
}

interface ComplianceResult {
  function_identifier: string;
  automation_name: string;
  schedule: string;
  cron: string;
  expected_invocations: number;
  actual_invocations: number;
  errors: number;
  compliance_pct: number | null;
  status: string;
  hours_back: number;
}

interface User {
  user_id: number;
  full_name: string;
  email: string;
  is_developer: number;
}

const ALERT_STATUSES = [
  { value: 'new', label: 'New', color: '#dc3545' },
  { value: 'in_progress', label: 'In Progress', color: '#007bff' },
  { value: 'waiting_on_information', label: 'Waiting on Info', color: '#ffc107' },
  { value: 'resolved', label: 'Resolved', color: '#28a745' },
  { value: 'resolved_in_review', label: 'Resolved - In Review', color: '#17a2b8' },
  { value: 'resolved_pending_deployment', label: 'Resolved - Pending Deployment', color: '#6f42c1' },
  { value: 'ignored', label: 'Ignored', color: '#6c757d' },
];

const Monitor: React.FC = () => {
  const [functions, setFunctions] = useState<MonitoredFunction[]>([]);
  const [alerts, setAlerts] = useState<MonitorAlert[]>([]);
  const [snapshots, setSnapshots] = useState<MonitorSnapshot[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('alerts');
  const [selectedFunction, setSelectedFunction] = useState<string | null>(null);
  const [discovering, setDiscovering] = useState(false);
  const [discoveredFunctions, setDiscoveredFunctions] = useState<DiscoverResult[]>([]);
  const [compliance, setCompliance] = useState<ComplianceResult[]>([]);
  const [complianceHours, setComplianceHours] = useState(24);
  const [developers, setDevelopers] = useState<User[]>([]);
  const [alertStatusFilter, setAlertStatusFilter] = useState('new,in_progress,waiting_on_information');
  const [expandedAlert, setExpandedAlert] = useState<number | null>(null);
  const [alertView, setAlertView] = useState<'cards' | 'kanban'>('cards');
  const [alertAssigneeFilter, setAlertAssigneeFilter] = useState<string>('all');
  const [draggedAlert, setDraggedAlert] = useState<MonitorAlert | null>(null);
  const [selectedAlerts, setSelectedAlerts] = useState<Set<number>>(new Set());

  useEffect(() => {
    fetchMonitorData();
    fetchDevelopers();

    // Auto-refresh alerts every 60 seconds
    const interval = setInterval(() => {
      fetchAlerts();
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchMonitorData = async () => {
    try {
      setLoading(true);
      const [functionsRes, alertsRes] = await Promise.all([
        automunityAPI.getMonitoredFunctions(),
        automunityAPI.getMonitorAlerts({ status: alertView === 'kanban' ? 'new,in_progress,waiting_on_information,resolved,resolved_in_review,resolved_pending_deployment' : alertStatusFilter })
      ]);

      const functionsData = await functionsRes.body.json();
      const alertsData = await alertsRes.body.json();

      setFunctions((functionsData as any)?.records || (Array.isArray(functionsData) ? functionsData : []));
      setAlerts((alertsData as any)?.records || (Array.isArray(alertsData) ? alertsData : []));
    } catch (err) {
      console.error('Error fetching monitor data:', err);
      toast.error('Failed to load monitoring data');
    } finally {
      setLoading(false);
    }
  };

  const fetchAlerts = async (statusFilter?: string) => {
    try {
      const res = await automunityAPI.getMonitorAlerts({ status: statusFilter || alertStatusFilter });
      const data = await res.body.json();
      setAlerts((data as any)?.records || (Array.isArray(data) ? data : []));
    } catch (err) {
      console.error('Error fetching alerts:', err);
    }
  };

  const fetchDevelopers = async () => {
    try {
      const res = await automunityAPI.getUsers();
      const data = await res.body.json();
      const records = (data as any)?.records || (Array.isArray(data) ? data : []);
      setDevelopers(records.filter((u: User) => u.is_developer === 1));
    } catch (err) {
      console.error('Error fetching developers:', err);
    }
  };

  const fetchSnapshots = async (functionName: string) => {
    try {
      const res = await automunityAPI.getMonitorSnapshots(functionName);
      const data = await res.body.json();
      setSnapshots((data as any)?.records || (Array.isArray(data) ? data : []));
      setSelectedFunction(functionName);
    } catch (err) {
      console.error('Error fetching snapshots:', err);
    }
  };

  const handleDiscover = async () => {
    setDiscovering(true);
    try {
      const res = await automunityAPI.discoverFunctions();
      const data = await res.body.json();
      setDiscoveredFunctions((data as any)?.untracked_functions || []);
      toast.success(`Found ${(data as any)?.untracked || 0} untracked functions`);
    } catch (err) {
      console.error('Error discovering functions:', err);
      toast.error('Failed to discover functions');
    } finally {
      setDiscovering(false);
    }
  };

  const fetchCompliance = async (hours: number = 24) => {
    try {
      const res = await automunityAPI.getMonitorCompliance(hours);
      const data = await res.body.json();
      const records = (data as any)?.records || (Array.isArray(data) ? data : []);
      setCompliance(Array.isArray(records) ? records : []);
    } catch (err) {
      console.error('Error fetching compliance:', err);
    }
  };

  const handleImportFunction = async (functionName: string) => {
    try {
      await automunityAPI.importFunction(functionName);
      toast.success(`Imported ${functionName}`);
      fetchMonitorData();
    } catch (err) {
      console.error('Error importing function:', err);
      toast.error('Failed to import function');
    }
  };

  const handleUpdateAlert = async (alertId: number, updates: { status?: string; assigned_to?: number | null }) => {
    try {
      await automunityAPI.updateAlert(alertId, updates);
      // Optimistic update: apply changes locally to avoid items disappearing
      setAlerts(prev => prev.map(a => 
        a.alert_id === alertId ? { ...a, ...updates } as MonitorAlert : a
      ));
      toast.success('Alert updated');
    } catch (err) {
      toast.error('Failed to update alert');
      fetchAlerts(); // Re-fetch on error to restore correct state
    }
  };

  const handleResolveAlert = async (alertId: number) => {
    await handleUpdateAlert(alertId, { status: 'resolved' });
  };

  const handleAlertDragStart = (e: React.DragEvent, alert: MonitorAlert) => {
    setDraggedAlert(alert);
    e.dataTransfer.effectAllowed = 'move';
    (e.target as HTMLElement).classList.add('dragging');
  };

  const handleAlertDragEnd = (e: React.DragEvent) => {
    (e.target as HTMLElement).classList.remove('dragging');
    setDraggedAlert(null);
  };

  const handleAlertDrop = (e: React.DragEvent, targetStatus: string) => {
    e.preventDefault();
    if (draggedAlert && draggedAlert.status !== targetStatus) {
      handleUpdateAlert(draggedAlert.alert_id, { status: targetStatus });
    }
    setDraggedAlert(null);
  };

  const filteredAlerts = alerts.filter(a => {
    if (alertAssigneeFilter === 'unassigned') return !a.assigned_to;
    if (alertAssigneeFilter !== 'all') return String(a.assigned_to) === alertAssigneeFilter;
    return true;
  });

  const toggleAlertSelection = (alertId: number) => {
    setSelectedAlerts(prev => {
      const next = new Set(prev);
      if (next.has(alertId)) next.delete(alertId); else next.add(alertId);
      return next;
    });
  };

  const selectAllAlerts = () => {
    if (selectedAlerts.size === filteredAlerts.length) {
      setSelectedAlerts(new Set());
    } else {
      setSelectedAlerts(new Set(filteredAlerts.map(a => a.alert_id)));
    }
  };

  const handleBulkAlertAction = async (updates: { status?: string; assigned_to?: number | null; action?: string }) => {
    if (selectedAlerts.size === 0) return;
    try {
      await automunityAPI.bulkUpdateAlerts(Array.from(selectedAlerts), updates);
      toast.success(`Updated ${selectedAlerts.size} alerts`);
      setSelectedAlerts(new Set());
      fetchAlerts();
    } catch (err) {
      toast.error('Bulk update failed');
    }
  };

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { color: string; label: string }> = {
      healthy: { color: '#28a745', label: 'Healthy' },
      warning: { color: '#ffc107', label: 'Warning' },
      critical: { color: '#dc3545', label: 'Critical' },
      missed: { color: '#fd7e14', label: 'Missed' },
      idle: { color: '#6c757d', label: 'Idle' },
    };
    const s = statusMap[status] || { color: '#6c757d', label: status || 'Unknown' };
    return (
      <span className="status-badge" style={{ backgroundColor: s.color }}>
        {s.label}
      </span>
    );
  };

  const healthySummary = functions.filter(f => f.latest_status === 'healthy').length;
  const warningSummary = functions.filter(f => ['warning', 'missed'].includes(f.latest_status)).length;
  const criticalSummary = functions.filter(f => f.latest_status === 'critical').length;
  const openAlerts = alerts.filter(a => !['resolved', 'resolved_in_review', 'resolved_pending_deployment', 'ignored'].includes(a.status)).length;

  if (loading) return <div className="loading">Loading monitoring data...</div>;

  return (
    <>
      <Toaster position="top-right" />
      <div className="monitor-container">
        <div className="monitor-header">
          <h1>Health Dashboard</h1>
          <p className="monitor-subtitle">Real-time health monitoring for your automation functions</p>
          <div className="monitor-addon-badge">
            <span className="addon-icon">🔌</span>
            <span>AWS Integration Add-on</span>
          </div>
        </div>

        <div className="monitor-summary">
          <div className="summary-card healthy">
            <div className="summary-icon">✅</div>
            <div className="summary-value">{healthySummary}</div>
            <div className="summary-label">Healthy</div>
          </div>
          <div className="summary-card warning">
            <div className="summary-icon">⚠️</div>
            <div className="summary-value">{warningSummary}</div>
            <div className="summary-label">Warning</div>
          </div>
          <div className="summary-card critical">
            <div className="summary-icon">🔴</div>
            <div className="summary-value">{criticalSummary}</div>
            <div className="summary-label">Critical</div>
          </div>
          <div className="summary-card alerts">
            <div className="summary-icon">🔔</div>
            <div className="summary-value">{openAlerts}</div>
            <div className="summary-label">Open Alerts</div>
          </div>
        </div>

        <div className="monitor-tabs">
          <button className={activeTab === 'alerts' ? 'active' : ''} onClick={() => setActiveTab('alerts')}>
            Alerts ({openAlerts})
          </button>
        </div>

        <div className="monitor-content">
          {activeTab === 'overview' && (
            <div className="functions-grid">
              {functions.length > 0 ? (
                <table className="monitor-table">
                  <thead>
                    <tr>
                      <th>Function</th>
                      <th>Automation</th>
                      <th>Status</th>
                      <th>Schedule</th>
                      <th>Last Checked</th>
                      <th>Invocations</th>
                      <th>Errors</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {functions.map((func) => (
                      <tr key={func.integration_config_id} className={`status-row-${func.latest_status}`}>
                        <td className="function-name">{func.function_identifier}</td>
                        <td>{func.automation_name}</td>
                        <td>{getStatusBadge(func.latest_status)}</td>
                        <td className="schedule-cell">{func.expected_schedule_description || '—'}</td>
                        <td>{func.last_checked_at ? new Date(func.last_checked_at).toLocaleString() : 'Never'}</td>
                        <td>{func.last_invocation_count ?? '—'}</td>
                        <td className={func.last_error_count > 0 ? 'error-count' : ''}>
                          {func.last_error_count ?? '—'}
                        </td>
                        <td>
                          <button 
                            className="btn btn-sm btn-primary"
                            onClick={() => fetchSnapshots(func.function_identifier)}
                          >
                            History
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="empty-state">
                  <p>No monitored functions yet. Use the Discover & Import tab to add functions.</p>
                </div>
              )}

              {selectedFunction && snapshots.length > 0 && (
                <div className="snapshot-panel">
                  <div className="snapshot-header">
                    <h3>History: {selectedFunction}</h3>
                    <button onClick={() => setSelectedFunction(null)} className="btn btn-sm btn-secondary">Close</button>
                  </div>
                  <table className="monitor-table compact">
                    <thead>
                      <tr>
                        <th>Time</th>
                        <th>Status</th>
                        <th>Invocations</th>
                        <th>Errors</th>
                        <th>Error Rate</th>
                        <th>Avg Duration</th>
                        <th>Max Duration</th>
                        <th>Throttles</th>
                      </tr>
                    </thead>
                    <tbody>
                      {snapshots.slice(0, 20).map((snap) => (
                        <tr key={snap.snapshot_id}>
                          <td>{new Date(snap.snapshot_time).toLocaleString()}</td>
                          <td>{getStatusBadge(snap.status)}</td>
                          <td>{snap.invocations}</td>
                          <td className={snap.errors > 0 ? 'error-count' : ''}>{snap.errors}</td>
                          <td>{snap.error_rate}%</td>
                          <td>{snap.avg_duration_ms?.toFixed(0)}ms</td>
                          <td>{snap.max_duration_ms?.toFixed(0)}ms</td>
                          <td>{snap.throttles}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'alerts' && (
            <div className="alerts-section">
              <div className="alerts-toolbar">
                <div className="alerts-filters">
                  <div className="view-toggle">
                    <button className={alertView === 'cards' ? 'active' : ''} onClick={() => setAlertView('cards')}>Cards</button>
                    <button className={alertView === 'kanban' ? 'active' : ''} onClick={() => setAlertView('kanban')}>Kanban</button>
                  </div>
                  {alertView === 'cards' && (
                    <select 
                      value={alertStatusFilter} 
                      onChange={e => { setAlertStatusFilter(e.target.value); fetchAlerts(e.target.value); }}
                      className="filter-select"
                    >
                      <option value="new,in_progress,waiting_on_information">Active (Queue)</option>
                      <option value="new">New</option>
                      <option value="in_progress">In Progress</option>
                      <option value="waiting_on_information">Waiting on Info</option>
                      <option value="resolved,resolved_in_review,resolved_pending_deployment">Resolved</option>
                      <option value="ignored">Ignored</option>
                      <option value="">All</option>
                    </select>
                  )}
                  <select
                    value={alertAssigneeFilter}
                    onChange={e => setAlertAssigneeFilter(e.target.value)}
                    className="filter-select"
                  >
                    <option value="all">All Assignees</option>
                    <option value="unassigned">Unassigned</option>
                    {developers.map(dev => (
                      <option key={dev.user_id} value={String(dev.user_id)}>{dev.full_name}</option>
                    ))}
                  </select>
                </div>
                <div className="alerts-count">
                  {filteredAlerts.length} alert{filteredAlerts.length !== 1 ? 's' : ''}
                </div>
              </div>

              {/* Bulk action bar */}
              {selectedAlerts.size > 0 && (
                <div className="bulk-action-bar">
                  <span className="bulk-count">{selectedAlerts.size} selected</span>
                  <select onChange={e => { if (e.target.value) { handleBulkAlertAction({ status: e.target.value }); e.target.value = ''; } }}>
                    <option value="">Change Status...</option>
                    {ALERT_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                  <select onChange={e => { if (e.target.value !== '') { handleBulkAlertAction({ assigned_to: e.target.value ? Number(e.target.value) : null }); e.target.value = ''; } }}>
                    <option value="">Assign To...</option>
                    <option value="0">Unassign</option>
                    {developers.map(dev => <option key={dev.user_id} value={dev.user_id}>{dev.full_name}</option>)}
                  </select>
                  <button className="btn btn-sm btn-danger" onClick={() => handleBulkAlertAction({ action: 'delete' })}>Delete</button>
                  <button className="btn btn-sm btn-secondary" onClick={() => setSelectedAlerts(new Set())}>Cancel</button>
                </div>
              )}

              {alertView === 'kanban' ? (
                <div className="alerts-kanban-board">
                  {ALERT_STATUSES.filter(s => !['ignored', 'resolved'].includes(s.value)).map(status => {
                    const columnAlerts = filteredAlerts.filter(a => a.status === status.value);
                    const isDropTarget = draggedAlert && draggedAlert.status !== status.value;
                    return (
                      <div
                        key={status.value}
                        className={`alerts-kanban-column ${isDropTarget ? 'drop-target' : ''}`}
                        onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
                        onDrop={e => handleAlertDrop(e, status.value)}
                      >
                        <div className="alerts-kanban-header" style={{ borderTopColor: status.color }}>
                          <span className="column-title">{status.label}</span>
                          <span className="column-count">{columnAlerts.length}</span>
                        </div>
                        <div className="alerts-kanban-body">
                          {columnAlerts.map(alert => (
                            <div
                              key={alert.alert_id}
                              className={`alerts-kanban-card severity-${alert.severity}`}
                              draggable
                              onDragStart={e => handleAlertDragStart(e, alert)}
                              onDragEnd={handleAlertDragEnd}
                            >
                              <div className="kanban-alert-header">
                                <span className={`severity-badge severity-${alert.severity}`}>{alert.severity?.toUpperCase()}</span>
                                {alert.occurrence_count > 1 && <span className="occurrence-badge">×{alert.occurrence_count}</span>}
                              </div>
                              <div className="kanban-alert-function">{alert.function_name}</div>
                              <div className="kanban-alert-error">{alert.error_summary || alert.message}</div>
                              <div className="kanban-alert-footer">
                                <select
                                  className="kanban-alert-assignee"
                                  value={alert.assigned_to || ''}
                                  onChange={e => { e.stopPropagation(); handleUpdateAlert(alert.alert_id, { assigned_to: e.target.value ? Number(e.target.value) : null }); }}
                                  onClick={e => e.stopPropagation()}
                                >
                                  <option value="">Unassigned</option>
                                  {developers.map(dev => (
                                    <option key={dev.user_id} value={dev.user_id}>{dev.full_name}</option>
                                  ))}
                                </select>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                /* Cards view (existing queue) */
                filteredAlerts.length > 0 ? (
                <div className="alerts-queue">
                  <label className="select-all-toggle">
                    <input type="checkbox" checked={selectedAlerts.size === filteredAlerts.length && filteredAlerts.length > 0} onChange={selectAllAlerts} />
                    Select All
                  </label>
                  {filteredAlerts.map((alert) => (
                    <div key={alert.alert_id} className={`alert-card severity-${alert.severity} ${selectedAlerts.has(alert.alert_id) ? 'selected' : ''}`}>
                      <div className="alert-card-header">
                        <div className="alert-card-left">
                          <input 
                            type="checkbox" 
                            checked={selectedAlerts.has(alert.alert_id)} 
                            onChange={() => toggleAlertSelection(alert.alert_id)}
                            className="alert-checkbox"
                          />
                          <span className={`severity-badge severity-${alert.severity}`}>
                            {alert.severity?.toUpperCase()}
                          </span>
                          <span className="alert-env-badge">{alert.app_env}</span>
                          <span className="alert-app-badge">{alert.app_abbr}</span>
                          {alert.occurrence_count > 1 && (
                            <span className="occurrence-badge">×{alert.occurrence_count}</span>
                          )}
                        </div>
                        <div className="alert-card-right">
                          <span className="alert-time">
                            {alert.last_occurrence_at 
                              ? new Date(alert.last_occurrence_at).toLocaleString() 
                              : new Date(alert.created_at).toLocaleString()}
                          </span>
                        </div>
                      </div>

                      <div className="alert-card-body">
                        <div className="alert-function-name">{alert.function_name}</div>
                        <div className="alert-error-summary">{alert.error_summary || alert.message}</div>
                        {alert.identifier && (
                          <div className="alert-identifier">{alert.identifier}</div>
                        )}
                      </div>

                      <div className="alert-card-actions">
                        <div className="alert-action-group">
                          <select
                            className="alert-status-select"
                            value={alert.status}
                            onChange={e => handleUpdateAlert(alert.alert_id, { status: e.target.value })}
                          >
                            {ALERT_STATUSES.map(s => (
                              <option key={s.value} value={s.value}>{s.label}</option>
                            ))}
                          </select>

                          <select
                            className="alert-assign-select"
                            value={alert.assigned_to || ''}
                            onChange={e => handleUpdateAlert(alert.alert_id, { 
                              assigned_to: e.target.value ? Number(e.target.value) : null 
                            })}
                          >
                            <option value="">Unassigned</option>
                            {developers.map(dev => (
                              <option key={dev.user_id} value={dev.user_id}>{dev.full_name}</option>
                            ))}
                          </select>
                        </div>

                        <div className="alert-quick-actions">
                          {alert.status === 'new' && (
                            <button 
                              className="btn btn-sm btn-primary"
                              onClick={() => handleUpdateAlert(alert.alert_id, { status: 'in_progress' })}
                            >
                              Start
                            </button>
                          )}
                          {alert.status !== 'resolved' && alert.status !== 'ignored' && (
                            <button 
                              className="btn btn-sm btn-success"
                              onClick={() => handleUpdateAlert(alert.alert_id, { status: 'resolved' })}
                            >
                              Resolve
                            </button>
                          )}
                          {alert.status !== 'ignored' && (
                            <button 
                              className="btn btn-sm btn-secondary"
                              onClick={() => handleUpdateAlert(alert.alert_id, { status: 'ignored' })}
                            >
                              Ignore
                            </button>
                          )}
                          <button
                            className="btn btn-sm btn-outline"
                            onClick={() => setExpandedAlert(expandedAlert === alert.alert_id ? null : alert.alert_id)}
                          >
                            {expandedAlert === alert.alert_id ? 'Less' : 'More'}
                          </button>
                        </div>
                      </div>

                      {expandedAlert === alert.alert_id && (
                        <div className="alert-card-expanded">
                          <div className="alert-detail-grid">
                            <div className="alert-detail-item">
                              <span className="detail-label">Source</span>
                              <span className="detail-value">{alert.source}</span>
                            </div>
                            <div className="alert-detail-item">
                              <span className="detail-label">Exception</span>
                              <span className="detail-value">{alert.exception_type || '—'}</span>
                            </div>
                            <div className="alert-detail-item">
                              <span className="detail-label">Automation</span>
                              <span className="detail-value">{alert.automation_name || '—'}</span>
                            </div>
                            <div className="alert-detail-item">
                              <span className="detail-label">First Seen</span>
                              <span className="detail-value">{new Date(alert.created_at).toLocaleString()}</span>
                            </div>
                            {alert.assigned_to_name && (
                              <div className="alert-detail-item">
                                <span className="detail-label">Assigned To</span>
                                <span className="detail-value">{alert.assigned_to_name}</span>
                              </div>
                            )}
                          </div>
                          {alert.message && (
                            <details className="alert-message-details">
                              <summary>Full Message</summary>
                              <pre className="alert-message-content">{alert.message}</pre>
                            </details>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  <p>🎉 No alerts matching this filter.</p>
                </div>
              )
              )}
            </div>
          )}

          {activeTab === 'history' && (
            <div className="history-section">
              <p>Select a function from the Functions tab to view its metric history.</p>
            </div>
          )}

          {activeTab === 'discover' && (
            <div className="discover-section">
              <div className="discover-header">
                <h3>Discover Lambda Functions</h3>
                <p>Find untracked Lambda functions in your AWS account and import them into Automunity.</p>
                <button 
                  className="btn btn-primary"
                  onClick={handleDiscover}
                  disabled={discovering}
                >
                  {discovering ? 'Scanning...' : 'Scan AWS Account'}
                </button>
              </div>

              {discoveredFunctions.length > 0 && (
                <div className="discovered-list">
                  <h4>Untracked Functions ({discoveredFunctions.length})</h4>
                  <table className="monitor-table">
                    <thead>
                      <tr>
                        <th>Function Name</th>
                        <th>Runtime</th>
                        <th>Description</th>
                        <th>Last Modified</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {discoveredFunctions.map((func) => (
                        <tr key={func.function_name}>
                          <td className="function-name">{func.function_name}</td>
                          <td>{func.runtime}</td>
                          <td>{func.description || '—'}</td>
                          <td>{func.last_modified ? new Date(func.last_modified).toLocaleDateString() : '—'}</td>
                          <td>
                            <button
                              className="btn btn-sm btn-success"
                              onClick={() => handleImportFunction(func.function_name)}
                            >
                              Import
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'compliance' && (
            <div className="compliance-section">
              <div className="compliance-header">
                <h3>Schedule Compliance</h3>
                <p>Expected vs actual invocations based on configured schedules.</p>
                <div className="compliance-controls">
                  <select value={complianceHours} onChange={e => { const h = Number(e.target.value); setComplianceHours(h); fetchCompliance(h); }}>
                    <option value={6}>Last 6 hours</option>
                    <option value={12}>Last 12 hours</option>
                    <option value={24}>Last 24 hours</option>
                    <option value={48}>Last 48 hours</option>
                    <option value={168}>Last 7 days</option>
                  </select>
                </div>
              </div>

              {compliance.length > 0 ? (
                <table className="monitor-table">
                  <thead>
                    <tr>
                      <th>Function</th>
                      <th>Automation</th>
                      <th>Schedule</th>
                      <th>Expected</th>
                      <th>Actual</th>
                      <th>Errors</th>
                      <th>Compliance</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {compliance.map((c) => (
                      <tr key={c.function_identifier} className={
                        c.compliance_pct !== null && c.compliance_pct < 50 ? 'status-row-critical' :
                        c.compliance_pct !== null && c.compliance_pct < 80 ? 'status-row-warning' : ''
                      }>
                        <td className="function-name">{c.function_identifier}</td>
                        <td>{c.automation_name}</td>
                        <td className="schedule-cell">{c.schedule}</td>
                        <td style={{ textAlign: 'center' }}>{c.expected_invocations}</td>
                        <td style={{ textAlign: 'center', fontWeight: 600 }}>{c.actual_invocations}</td>
                        <td className={c.errors > 0 ? 'error-count' : ''} style={{ textAlign: 'center' }}>{c.errors}</td>
                        <td style={{ textAlign: 'center' }}>
                          {c.compliance_pct !== null ? (
                            <span className="compliance-badge" style={{
                              backgroundColor: c.compliance_pct >= 90 ? '#28a745' : c.compliance_pct >= 70 ? '#ffc107' : '#dc3545',
                              color: c.compliance_pct >= 70 && c.compliance_pct < 90 ? '#333' : 'white'
                            }}>
                              {c.compliance_pct}%
                            </span>
                          ) : '—'}
                        </td>
                        <td>{getStatusBadge(c.status)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="empty-state">
                  <p>No scheduled functions found, or no snapshot data available yet.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Monitor;
